import QuestionMarkIcon from "@/assets/icons/QuestionMarkIcon";
import CollapsibleArea from "@/components/common/CollapsibleArea";
import {
  StyledAccordionItem,
  StyledAccordionItemContent,
  StyledAccordionTrigger,
  StyledMarkdown,
  useIsMobile,
} from "@/ui";
import { leftAccentCardProps, surfaceCardProps, surfaceInsetPaperProps } from "@canucci/base-mantine/theme";
import { Accordion, Box, Paper, SimpleGrid, Stack, Text, Title } from "@mantine/core";
import type { SimpleGridProps } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./FocusSection.module.css";

function SupportingQuotes({ items }: { items: string[] }) {
  return (
    <Stack gap="md" p="xl" pt={0}>
      {items.map((item, index) => (
        <Paper key={index} {...surfaceInsetPaperProps} p="md">
          <Text fz="lg" lh={1.5} c="var(--text-on-dark)">
            &ldquo;{item}&rdquo;
          </Text>
        </Paper>
      ))}
    </Stack>
  );
}

function FocusAreaSummarySection({
  title,
  content,
  minHeight,
}: {
  title?: string;
  content: string;
  minHeight?: string;
}) {
  return (
    <Paper {...leftAccentCardProps} mt="xl" p="xl" mih={minHeight} h="100%">
      {title ? (
        <Title fz="lg" fw="bold" mb="md" c="var(--text-on-dark)">
          {title}
        </Title>
      ) : null}
      <Box c="var(--text-on-dark)">
        <CollapsibleArea collapseHeight="24rem">
          <StyledMarkdown content={content} paragraphTextProps={{ c: "var(--text-on-dark)" }} />
        </CollapsibleArea>
      </Box>
    </Paper>
  );
}

function FocusAreaQuotesSection({
  title,
  content,
  isExpanded,
}: {
  title: string;
  content: string[];
  isExpanded: boolean;
}) {
  const isMobile = useIsMobile();

  return (
    <Paper {...surfaceCardProps} mt="xl" p={0} h="100%">
      <StyledAccordionItem value="quotes" bg="var(--surface-raised)">
        <StyledAccordionTrigger
          isExpanded={isExpanded}
          title={title}
          iconFn={() => <QuestionMarkIcon color="var(--text-on-dark)" />}
          description=""
          hideCloseButton={!isMobile}
          textColor="var(--text-on-dark)"
          backgroundColor="var(--surface-raised)"
          p="md"
        >
          <Title fz="lg" fw="bold">
            {title}
          </Title>
        </StyledAccordionTrigger>
        <StyledAccordionItemContent bg="var(--surface-raised)">
          <SupportingQuotes items={content} />
        </StyledAccordionItemContent>
      </StyledAccordionItem>
    </Paper>
  );
}

export function FocusAreaDetailView({
  title,
  content,
  quotes,
  minHeight,
  spacing,
}: {
  title?: string;
  content: string;
  quotes: string[];
  minHeight?: string;
  spacing?: SimpleGridProps["spacing"];
}) {
  const isMobile = useIsMobile();
  const { t } = useTranslation("pages");
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const quotesExpanded = !isMobile || expandedItems.includes("quotes");

  return (
    <SimpleGrid cols={{ base: 1, md: quotes.length > 0 ? 2 : 1 }} spacing={spacing}>
      <FocusAreaSummarySection title={title} content={content} minHeight={minHeight} />
      {quotes.length > 0 ? (
        <Accordion
          value={quotesExpanded ? ["quotes"] : []}
          multiple
          onChange={setExpandedItems}
          className={styles.quotesAccordion}
        >
          <FocusAreaQuotesSection
            title={t("canvas.focusArea.quotesTitle")}
            content={quotes}
            isExpanded={quotesExpanded}
          />
        </Accordion>
      ) : null}
    </SimpleGrid>
  );
}
